import { useEffect, useRef, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { ClipboardList, Users, Timer, MessageSquare, ShieldCheck, Printer } from "lucide-react";
import { api } from "@/lib/api";
import { trackProductView } from "@/lib/attribution";
import { trackEvent, trackChalkos } from "@/lib/analytics";
import Seo from "@/components/Seo";
import KitIntakeForm from "@/components/starterkit/KitIntakeForm";
import NotifyMe from "@/components/starterkit/NotifyMe";
import { ProductFaq, PairsWell, PreviewGallery } from "@/components/product/ProductSections";

const INSIDE = [
  [ClipboardList, "Two first-week practice plans", "Minute-by-minute, built for your age group and practice length. Printable one-page cards for the gym."],
  [Users, "Playing-time rotation sheet", "Built for your roster and the number of players on the court in your league. Fair, already worked out."],
  [Timer, "8-week skill roadmap", "What to teach each week so the season builds. No guessing."],
  [MessageSquare, "Parent meeting + welcome message", "Word-for-word scripts so the first conversation goes right."],
  [ShieldCheck, "Cheat sheet + game-day checklist", "What to say, what never to say, and what to bring."],
  [Printer, "When things get hard", "Scripts for the tough moments, from Coach Kyto."],
];

export default function StarterKit() {
  const [opts, setOpts] = useState(null);
  const [params] = useSearchParams();
  const { hash } = useLocation();
  const guideClick = useRef(false);
  useEffect(() => {
    if (guideClick.current || params.get("utm_source") !== "rookie_guide") return;
    guideClick.current = true;
    trackChalkos("starter_kit_click_from_guide", { campaign: params.get("utm_campaign") || "" });
  }, [params]);
  useEffect(() => {
    if (!opts || hash !== "#notify") return;
    const t = setTimeout(() => document.getElementById("notify")?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    return () => clearTimeout(t);
  }, [opts, hash]);
  useEffect(() => {
    trackProductView(api, "starter_kit");
    trackEvent("view_item", { currency: "USD", value: 9, items: [{ item_id: "starter_kit", item_name: "Just Got Voluntold Starter Kit" }] });
  }, []);
  useEffect(() => { api.get("/starter-kit/options").then((r) => setOpts(r.data)).catch(() => setOpts({ combos: [], price_cents: 900 })); }, []);
  const [all, setAll] = useState([]);
  useEffect(() => { api.get("/products").then(({ data }) => setAll(data || [])).catch(() => {}); }, []);
  const price = opts ? `$${(opts.price_cents / 100).toFixed(0)}` : "$9";

  return (
    <main className="bg-[color:var(--vt-cream)]" data-testid="starter-kit-page">
      <Seo title="Just Got Voluntold Starter Kit" description="A $9 personalized kit for brand-new volunteer coaches: practice plans, rotation sheet, parent scripts and more." path="/starter-kit"
        jsonLd={{ "@context": "https://schema.org", "@type": "Product", name: "Just Got Voluntold Starter Kit", sku: "starter_kit",
          description: "Personalized first-week practice plans, rotation sheet and parent scripts for brand-new volunteer basketball coaches (ages 4-14).",
          brand: { "@type": "Brand", name: "Voluntold" },
          offers: { "@type": "Offer", price: "9.00", priceCurrency: "USD", availability: "https://schema.org/InStock", url: "https://voluntoldhq.com/starter-kit" } }} />
      <section className="max-w-6xl mx-auto px-5 pt-16 pb-12 grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-start">
        <div className="rise">
          <p className="text-xs uppercase tracking-[0.2em] text-[color:var(--vt-amber)] font-[IBM_Plex_Mono]">The Starter Kit · {price}</p>
          <h1 className="font-head uppercase text-4xl sm:text-5xl lg:text-6xl text-[color:var(--vt-dark)] leading-[0.95] mt-3">You just got voluntold.<br />Here's your first week.</h1>
          <p className="mt-4 text-sm font-head uppercase tracking-wide text-[color:var(--vt-dark)]">Know a new coach? Send it as a gift.</p>
          <p className="mt-6 text-base text-[color:var(--vt-body-on-cream)] max-w-xl">Tell us your sport, your kids' ages, and how long you practice. You get a Coach's Guide and printable Practice Cards built for your team, in your inbox within minutes. Every drill was written and checked by a human coach. Nothing is made up by AI.</p>
          {params.get("canceled") && <p className="mt-4 text-sm text-[#B3261E]" data-testid="sk-checkout-canceled">Checkout canceled. Your kit wasn't charged. Pick up where you left off below.</p>}
          <div className="mt-10 grid sm:grid-cols-2 gap-x-8 gap-y-6">
            {INSIDE.map(([Icon, t, d], i) => (
              <div key={t} className="flex gap-3 rise" style={{ animationDelay: `${120 + i * 60}ms` }}>
                <Icon size={20} className="text-[color:var(--vt-amber)] shrink-0 mt-0.5" />
                <div><p className="font-head uppercase text-sm text-[color:var(--vt-dark)]">{t}</p><p className="text-sm text-[color:var(--vt-body-on-cream)]">{d}</p></div>
              </div>
            ))}
          </div>
        </div>
        <div className="lg:sticky lg:top-24 rise" style={{ animationDelay: "200ms" }}>
          {opts ? <KitIntakeForm opts={opts} price={price} initial={{ sport: params.get("sport"), age: params.get("age") }} /> : <div className="h-96 border-2 border-[color:var(--vt-dark)] animate-pulse" />}
          {opts && <div className="mt-8 scroll-mt-24" id="notify" data-testid="sk-notify-anchor"><NotifyMe sports={opts.coming_soon} ages={[...new Set(opts.combos.map((c) => c.age))]} /></div>}
        </div>
      </section>
      <div className="max-w-6xl mx-auto px-5 pb-16">
        <PreviewGallery images={[
          { src: "/products/preview-starter_kit-1.png", alt: "Starter Kit sample page: your first two practice plans" },
          { src: "/products/preview-starter_kit-2.png", alt: "Starter Kit sample page: practice card" },
        ]} />
        <ProductFaq k="starter_kit" />
        <PairsWell k="starter_kit" products={all} />
      </div>
    </main>
  );
}
